// v10/src/hooks/useWebAuthn.js
import { useState, useCallback, useEffect } from 'react';
import {
  startRegistration,
  startAuthentication,
} from '@simplewebauthn/browser';

const API = process.env.REACT_APP_API_URL || 'http://localhost:5000';

const useWebAuthn = () => {
  const [loading,       setLoading]       = useState(false);
  const [error,         setError]         = useState(null);
  const [hasCredential, setHasCredential] = useState(
    !!localStorage.getItem('condor360_webauthn_registered')
  );

  const supported = !!(
    window.PublicKeyCredential &&
    navigator.credentials?.get
  );

  // ✅ FIX: soporte para ?reset_webauthn=1 en la URL
  // Permite limpiar el flag sin acceso a la consola del navegador
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('reset_webauthn')) {
      localStorage.removeItem('condor360_webauthn_registered');
      localStorage.removeItem('condor360_webauthn_email');
      setHasCredential(false);
      window.history.replaceState({}, '', '/');
      console.log('[WebAuthn] Credenciales locales reseteadas por parámetro URL');
    }
  }, []);

  // ── Registrar dispositivo biométrico ──────────────────────────
  const registerBiometric = useCallback(async (authToken, friendlyName = 'Mi dispositivo') => {
    setLoading(true); setError(null);
    try {
      const optRes = await fetch(`${API}/api/webauthn/register/options`, {
        method:  'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${authToken}` },
      });
      const { options } = await optRes.json();

      const regResponse = await startRegistration(options);

      const verRes = await fetch(`${API}/api/webauthn/register/verify`, {
        method:  'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${authToken}` },
        body:    JSON.stringify({ response: regResponse, friendlyName }),
      });
      const data = await verRes.json();
      if (!data.success) throw new Error(data.message || 'Error al registrar');

      // ✅ Solo marcamos como registrado si el servidor confirmó el éxito
      localStorage.setItem('condor360_webauthn_registered', '1');
      setHasCredential(true);
      return true;
    } catch (err) {
      const msg = err.name === 'NotAllowedError'
        ? 'Autenticación biométrica cancelada.'
        : err.message || 'Error al registrar el dispositivo.';
      setError(msg);
      throw new Error(msg);
    } finally { setLoading(false); }
  }, []);

  // ── Login biométrico ───────────────────────────────────────────
  const biometricLogin = useCallback(async (email) => {
    setLoading(true); setError(null);
    try {
      const optRes = await fetch(`${API}/api/webauthn/login/options`, {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        body:    JSON.stringify({ email }),
      });
      const optData = await optRes.json();

      // ✅ FIX: si el servidor dice que no hay credencial registrada,
      // limpiar el flag local para que el botón de huella desaparezca
      if (!optData.success) {
        if (optRes.status === 404 || optData.message?.includes('No hay dispositivo')) {
          localStorage.removeItem('condor360_webauthn_registered');
          localStorage.removeItem('condor360_webauthn_email');
          setHasCredential(false);
        }
        throw new Error(optData.message || 'No hay dispositivo biométrico registrado.');
      }

      const { options, userId } = optData;

      const authResponse = await startAuthentication(options);

      const verRes = await fetch(`${API}/api/webauthn/login/verify`, {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        body:    JSON.stringify({ userId, response: authResponse }),
      });
      const data = await verRes.json();
      if (!data.success) throw new Error(data.message || 'Verificación biométrica fallida.');

      return { user: data.user, token: data.token };
    } catch (err) {
      const msg = err.name === 'NotAllowedError'
        ? 'Autenticación biométrica cancelada.'
        : err.name === 'InvalidStateError'
        ? 'Este dispositivo no tiene una credencial registrada.'
        : err.message || 'Error de autenticación biométrica.';
      setError(msg);
      throw new Error(msg);
    } finally { setLoading(false); }
  }, []);

  return { supported, hasCredential, loading, error, registerBiometric, biometricLogin };
};

export default useWebAuthn;
